import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Grid, Paper, Typography, Button } from '@material-ui/core';

import { ArchivalUnitCopy } from 'types';
import { ReadOnlyField } from 'components/read-only-field/read-only-field';
import { RegisterField } from 'modules/register/register-field';
import { handleOriginatorLink } from 'modules/register/reqister-utils';
import { StructMap } from 'components/struct-map/struct-map';
import { StructMapData } from 'components/struct-map/struct-map-types';
import { LinkField } from 'components/link-field/link-field';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
    },
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    archivalUnitInfo: {
      marginTop: 10,
      fontWeight: 400,
      color: theme.palette.primary.main,
    },
    content: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
  })
);

export function ArchivalUnitDetail({
  item,
}: {
  item?: ArchivalUnitCopy & { structMap?: StructMapData };
}) {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    numericId,
    archivalUnitReference,
    summary,
    timeRange,
    evidenceTypeName,
    structMap,
    originator,
    zipFile,
    aipData = [],
  } = item || {};

  const {
    archivalCollection,
    entityCharacter,
    accessType,
    structuredLink,
    note,
    storageUnits = [],
    describingArchivalAids = [],
  } = archivalUnitReference || {};

  const archivalAid =
    describingArchivalAids.length > 0 ? describingArchivalAids[0] : undefined;

  const downloadZippedContent = useCallback(async () => {
    window.open(
      process.env.PUBLIC_URL + `/api/file/archival/${zipFile?.id}`,
      '_blank'
    );
  }, [zipFile]);

  return (
    <>
      <Typography variant="h5">{summary}</Typography>
      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <Typography variant="h6">
            {formatMessage({
              id: 'archivalUnit.inclusion',
            })}
          </Typography>
          <br />
          <br />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollection',
            })}
            value={archivalCollection?.name}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          <LinkField
            label={formatMessage({
              id: 'archivalUnit.archivalCollectionNumber',
            })}
            value={archivalCollection?.evidenceNumber}
            href={`fondy/0/${archivalCollection?.id}`}
          />
          {archivalAid && (
            <LinkField
              label={formatMessage({
                id: 'archivalUnit.archivalAid',
              })}
              value={archivalAid?.name}
              href={`pomucky/0/${archivalAid?.id}`}
            />
          )}
        </Grid>
      </Paper>

      <Paper elevation={0} className={classes.paper}>
        <Grid container className={classes.root}>
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit',
            })}
            value={numericId?.toString()}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.regest',
            })}
            value={summary}
            alwaysShow={true}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.referenceDesignation',
            })}
            value={structuredLink}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.type',
            })}
            value={evidenceTypeName}
          />
          <ReadOnlyField
            label={formatMessage({ id: 'archivalUnit.form' })}
            value={formatMessage({
              id: `archivalUnit.entityCharacter.${entityCharacter}`,
            })}
          />
          <ReadOnlyField
            label={formatMessage({ id: 'archivalUnit.digitalization' })}
            Component={
              <AccessibleDigitalContent
                hasDigitalContent={aipData.length > 0}
                structMapData={structMap}
              />
            }
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.dating',
            })}
            value={timeRange}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.storageUnit',
            })}
            value={
              storageUnits.length > 0
                ? storageUnits.map((s) => s).join(', ')
                : undefined
            }
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.accessibility',
            })}
            value={formatMessage({
              id: `archivalUnit.accessType.${accessType}`,
              defaultMessage: `${accessType}`,
            })}
          />
          <ReadOnlyField
            label={formatMessage({
              id: 'archivalUnit.note',
            })}
            value={note}
          />
        </Grid>
      </Paper>

      {originator && (
        <Paper elevation={0} className={classes.paper}>
          <Grid container className={classes.root}>
            <Typography variant="h6">
              {formatMessage({
                id: 'archivalUnit.indexEntries',
              })}
            </Typography>
            <br />
            <br />

            <RegisterField
              label={formatMessage({
                id: 'archivalUnit.originator',
              })}
              withoutBorder={true}
              value={originator?.preferredName?.label}
              valueLink={originator && handleOriginatorLink(originator, true)}
            />
          </Grid>
        </Paper>
      )}

      {structMap !== undefined && (
        <Paper elevation={0} className={classes.paper} id="digital-content">
          <Grid container className={classes.root}>
            <div className={classes.content}>
              <Typography variant="h6">
                {formatMessage({ id: 'archivalUnit.content' })}
              </Typography>
              {zipFile && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={downloadZippedContent}
                >
                  {formatMessage({
                    id: 'archivalUnit.downloadAllContent',
                  })}
                </Button>
              )}
            </div>

            <StructMap
              data={structMap}
              onChange={() => {}}
              parentEntity={item!}
            />
          </Grid>
        </Paper>
      )}
    </>
  );
}

export function AccessibleDigitalContent({
  hasDigitalContent,
  structMapData,
}: {
  hasDigitalContent: boolean;
  structMapData?: StructMapData;
}) {
  const { formatMessage } = useIntl();

  if (hasDigitalContent) {
    if (structMapData) {
      return (
        <a href="#digital-content" style={{ color: '#cf2931' }}>
          {formatMessage({ id: 'true' })}
        </a>
      );
    } else {
      return <span>{formatMessage({ id: 'true' })}</span>;
    }
  }

  return (
    <span>
      {formatMessage({
        id: 'false',
      })}
    </span>
  );
}
